import { useEffect, useMemo, useState } from 'react';
import { Loader2, Paperclip } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { apiClient } from '@features/shared/api';
import { useQuotationDraftStore } from '../../store/quotationDraftStore';
import type { QuotationSourceEmailSummary, QuotationSourceEmailAttachment } from '../../types/quotation.types';
import { guessPreviewKind } from '../../utils/previewKind';
import { GmailThreadView } from './GmailThreadView';
import { ReviewImageViewer } from './ReviewImageViewer';
import { ReviewPdfViewer } from './ReviewPdfViewer';
import { ReviewSpreadsheetGridViewer } from './ReviewSpreadsheetGridViewer';
import { ReviewTextViewer } from './ReviewTextViewer';
import { ReviewUnavailableState } from './ReviewUnavailableState';

// Default selection used as the baseline for toggling and as the implicit "include all
// real attachments" set when sales hasn't picked anything explicitly. Mirrors the backend
// default in QuotationService.RetryExtractionAsync — keep them in sync.
function defaultSelectedIds(attachments: QuotationSourceEmailAttachment[]): number[] {
  return attachments.filter((a) => !a.isLikelySignature).map((a) => a.id);
}

interface EmailReviewSourcePanelProps {
  sourceEmail: QuotationSourceEmailSummary;
}

// Lazy-fetches attachment bytes from the email-automation lazy-fetch endpoint and renders
// them with the existing review viewers (PDF, image, spreadsheet). Reuses the same blob-URL
// lifecycle that ReviewSourcePanel uses so StrictMode's double-invoke doesn't revoke a live
// URL.
function useAttachmentBlob(emailId: number, attachment: QuotationSourceEmailAttachment | null) {
  const [blob, setBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!attachment) {
      setBlob(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setBlob(null);
    void apiClient
      .get<Blob>(`/email-automation/attachment/${emailId}/${attachment.id}`, { responseType: 'blob' })
      .then((response) => {
        if (cancelled) return;
        setBlob(response.data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load attachment');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [emailId, attachment]);

  return { blob, error, loading };
}

interface AttachmentViewerProps {
  emailId: number;
  attachment: QuotationSourceEmailAttachment;
}

function AttachmentViewer({ emailId, attachment }: AttachmentViewerProps) {
  const { blob, error, loading } = useAttachmentBlob(emailId, attachment);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const file = useMemo(
    () => (blob ? new File([blob], attachment.originalFileName, { type: attachment.mimeType }) : null),
    [blob, attachment.originalFileName, attachment.mimeType],
  );

  useEffect(() => {
    if (!blob) {
      setObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(blob);
    setObjectUrl(url);
    return () => {
      URL.revokeObjectURL(url);
      setObjectUrl((current) => (current === url ? null : current));
    };
  }, [blob]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading {attachment.originalFileName}…
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex h-full items-center justify-center px-4 text-center text-sm text-destructive">
        {error}
      </div>
    );
  }
  if (!objectUrl || !file) {
    return <ReviewUnavailableState fileName={attachment.originalFileName} />;
  }

  const kind = guessPreviewKind(attachment.originalFileName);
  if (kind === 'image') return <ReviewImageViewer src={objectUrl} alt={attachment.originalFileName} />;
  if (kind === 'pdf') return <ReviewPdfViewer src={objectUrl} fileName={attachment.originalFileName} />;
  if (kind === 'spreadsheet') {
    return <ReviewSpreadsheetGridViewer file={file} fileName={attachment.originalFileName} />;
  }
  // Other file types — surface "unavailable" so sales knows download is the only path.
  return <ReviewUnavailableState fileName={attachment.originalFileName} />;
}

export function EmailReviewSourcePanel({ sourceEmail }: EmailReviewSourcePanelProps) {
  const attachments = sourceEmail.attachments;
  const activeAttachmentId = useQuotationDraftStore((s) => s.reviewActiveAttachmentId);
  const setActiveAttachmentId = useQuotationDraftStore((s) => s.setReviewActiveAttachmentId);
  const selectedIds = useQuotationDraftStore((s) => s.reviewSelectedAttachmentIds);
  const setSelectedIds = useQuotationDraftStore((s) => s.setReviewSelectedAttachmentIds);
  const toggleSelectedId = useQuotationDraftStore((s) => s.toggleReviewSelectedAttachmentId);

  const defaults = useMemo(() => defaultSelectedIds(attachments), [attachments]);

  // On first mount for this email: seed the preview to the first real (non-signature)
  // attachment, and seed the selection set to "all real attachments" so the trivial
  // case (one PDF, or N photos of one RFQ) extracts on a single Retry click without
  // forcing sales to multi-select. Reset on unmount so the next email starts fresh.
  useEffect(() => {
    if (activeAttachmentId == null && attachments.length > 0) {
      const firstReal = attachments.find((a) => !a.isLikelySignature) ?? attachments[0];
      setActiveAttachmentId(firstReal.id);
    }
    if (selectedIds == null) {
      // Pass null on this initial seed to mean "use backend default" — we render UI
      // chrome based on `effectiveSelectedIds` which materializes the default below
      // when sales hasn't toggled anything yet.
      setSelectedIds(null);
    }
    return () => {
      setActiveAttachmentId(null);
      setSelectedIds(null);
    };
    // We intentionally only re-run when the email changes (id), not on every render —
    // selecting / toggling via pill click sets the store directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceEmail.id]);

  // Materialize the "selected" set so the UI checkboxes always reflect a concrete set.
  // null in the store = "use backend default"; render that default state in the UI so
  // sales can see what would happen on Retry without first having to click anything.
  const effectiveSelectedIds = selectedIds ?? defaults;

  const activeAttachment =
    attachments.find((a) => a.id === activeAttachmentId) ?? null;

  return (
    // CSC-294: absolute-fill the parent source-pane wrapper. The wrapper at
    // QuotationReviewView uses `xl:h-auto` (relies on flex-stretch for visual height),
    // so an `h-full` chain inside collapses to content height at the xl breakpoint —
    // which left the Conversation pane short and the gray bg failing to fill. The
    // wrapper has `relative`, so absolute inset-0 here gives Tabs a definite,
    // row-stretched height that h-full / flex-1 can chain off of reliably.
    <Tabs defaultValue="conversation" className="absolute inset-0 flex flex-col">
      <div className="border-b px-3 pt-2">
        <TabsList>
          {/* CSC-294: Conversation replaces the legacy "Email content" tab. The legacy
              tab only ever showed bodyText for the *originating* AutoDraft message;
              this one fetches the entire Gmail thread (sender, date, full HTML/text
              body per message) and is prefetched on master-list row hover so the tab
              renders instantly. */}
          <TabsTrigger value="conversation">Conversation</TabsTrigger>
          <TabsTrigger value="attachments">
            <Paperclip className="mr-1 h-3 w-3" />
            Attachments
            {attachments.length > 0 && (
              <span className="ml-1.5 rounded bg-muted px-1.5 py-0.5 text-xs">
                {attachments.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>
      </div>
      <TabsContent
        value="conversation"
        // CSC-294: data-state-gated display. Radix renders both tab panels in the DOM
        // and relies on the `hidden` attribute to hide inactive ones — but a Tailwind
        // `flex` class overrides `hidden` (CSS class > HTML attribute), so both panels
        // would render at the same time and split the height. The data-state variants
        // make `display: flex` apply only when the tab is active; inactive falls back
        // to `display: none` via Radix's hidden attribute.
        className="m-0 flex-1 flex-col overflow-hidden data-[state=active]:flex data-[state=inactive]:hidden"
      >
        {sourceEmail.gmailThreadId ? (
          <GmailThreadView threadId={sourceEmail.gmailThreadId} />
        ) : sourceEmail.bodyText ? (
          // Legacy fallback: row predates threadId capture (vanishingly rare). Still
          // show the captured body text so sales has something to read.
          <ReviewTextViewer text={sourceEmail.bodyText} />
        ) : (
          <div className="flex h-full items-center justify-center text-sm italic text-muted-foreground">
            No email body captured.
          </div>
        )}
      </TabsContent>
      <TabsContent
        value="attachments"
        // Same data-state-gated display as the Conversation tab — see comment above.
        className="m-0 flex-1 flex-col overflow-hidden data-[state=active]:flex data-[state=inactive]:hidden"
      >
        {attachments.length === 0 ? (
          <ReviewUnavailableState fileName={null} />
        ) : (
          <>
            {attachments.length > 0 && (
              <div className="flex flex-col gap-1.5 border-b px-3 py-2">
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                  <span>
                    <strong>{effectiveSelectedIds.length}</strong> of{' '}
                    <strong>{attachments.length}</strong> included in extraction
                  </span>
                  <button
                    type="button"
                    className="text-xs underline-offset-2 hover:underline"
                    onClick={() => setSelectedIds(defaults)}
                  >
                    Reset to default
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {attachments.map((att) => {
                    const isActive = att.id === activeAttachmentId;
                    const isSelected = effectiveSelectedIds.includes(att.id);
                    // CSC-291: select and view are now distinct actions.
                    //   • Checkbox toggles inclusion in the extraction set.
                    //   • Clicking the badge changes which file the preview shows.
                    // Sales can therefore preview any attachment (including one flagged
                    // as a signature) without altering what gets sent to extraction.
                    return (
                      <div
                        key={att.id}
                        className={`flex items-center gap-1.5 rounded-md border px-1.5 py-0.5 ${
                          isActive ? 'border-primary bg-primary/5' : 'border-border'
                        }`}
                      >
                        <Checkbox
                          id={`att-select-${att.id}`}
                          checked={isSelected}
                          onCheckedChange={() => toggleSelectedId(att.id, defaults)}
                          aria-label={
                            isSelected
                              ? `Exclude ${att.originalFileName} from extraction`
                              : `Include ${att.originalFileName} in extraction`
                          }
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setActiveAttachmentId(att.id)}
                          className="h-6 gap-1 px-1 text-xs"
                          title={
                            att.isLikelySignature
                              ? 'Looks like an email signature asset — tick the checkbox to include it anyway'
                              : 'Click to preview'
                          }
                        >
                          <span>{att.originalFileName}</span>
                          {att.isLikelySignature && (
                            <span className="ml-1 rounded border border-current/40 px-1 py-px text-[10px] uppercase tracking-wide opacity-80">
                              signature
                            </span>
                          )}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="flex-1 overflow-hidden">
              {activeAttachment && (
                <AttachmentViewer emailId={sourceEmail.id} attachment={activeAttachment} />
              )}
            </div>
          </>
        )}
      </TabsContent>
    </Tabs>
  );
}

